import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { setSession } from "@/lib/sessionStore";
import { ShieldCheck, EnvelopeSimple, Sparkle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABEL = {
  admin: "Administrador",
  operator: "Operador",
  viewer: "Consulta",
};

export default function AcceptInvite() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser, setTenant } = useAuth();
  const params = new URLSearchParams(loc.search);
  const token = params.get("token") || "";

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirm: false });

  // Validation rules — keep in sync with backend (8+ chars).
  const passwordIssue = password.length === 0
    ? null
    : password.length < 8
      ? "A senha precisa ter pelo menos 8 caracteres."
      : null;
  const confirmIssue = confirm.length === 0
    ? null
    : confirm !== password
      ? "As senhas não conferem."
      : null;

  useEffect(() => {
    let cancelled = false;
    if (!token) { setLoading(false); return; }
    api.get(`/team/invites/info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch(() => { if (!cancelled) setPreview({ valid: false, reason: "error" }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    // Mark all fields as touched so any inline error becomes visible
    setTouched({ password: true, confirm: true });
    if (password.length < 8) {
      toast.error("A senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas não conferem.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/team/invites/accept", { token, password });
      setSession({ token: data.token, user: data.user, tenant: data.tenant });
      setUser(data.user);
      setTenant(data.tenant);
      toast.success(`Bem-vindo(a), ${data.user.name}!`);
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível aceitar o convite.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredCard>Validando convite…</CenteredCard>;
  }
  if (!token || !preview?.valid) {
    const reason = preview?.reason;
    const msg = reason === "expired" ? "Este convite expirou. Peça ao administrador para enviar um novo."
      : reason === "already_used" ? "Este convite já foi usado. Faça login normalmente."
      : reason === "revoked" ? "Este convite foi cancelado pelo administrador."
      : "Link inválido — verifique se você copiou o link completo do email.";
    return (
      <CenteredCard>
        <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
          <X size={22} weight="bold" />
        </div>
        <h1 className="text-xl font-heading font-semibold text-slate-900 mt-4 text-center">Convite inválido</h1>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed text-center">{msg}</p>
        <button onClick={() => nav("/login")} className="mt-6 w-full py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
          Ir para o login
        </button>
      </CenteredCard>
    );
  }

  return (
    <CenteredCard>
      <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mx-auto">
        <ShieldCheck size={22} weight="duotone" />
      </div>
      <h1 className="text-xl font-heading font-semibold text-slate-900 mt-4 text-center">
        Bem-vindo(a) ao {preview.tenant_name}
      </h1>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed text-center">
        Você foi convidado(a) como <b>{ROLE_LABEL[preview.role] || preview.role}</b>. Crie sua senha para entrar.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-3" data-testid="accept-invite-form" noValidate>
        <div className="px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 flex items-center gap-2">
          <EnvelopeSimple size={14} className="text-slate-500" />
          {preview.email}
        </div>

        <div>
          <input
            type="password"
            placeholder="Crie sua senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            data-testid="accept-invite-password"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${touched.password && passwordIssue ? "border-rose-400 focus:ring-rose-500" : "border-slate-300 focus:ring-slate-900"}`}
          />
          <div className="mt-1.5 flex items-center gap-1.5 min-h-[16px] text-[11.5px]">
            {touched.password && passwordIssue ? (
              <span className="inline-flex items-center gap-1 text-rose-600" data-testid="accept-invite-password-error">
                <span className="w-1 h-1 rounded-full bg-rose-500" />
                {passwordIssue}
              </span>
            ) : (
              <span className="text-slate-500">Mínimo de 8 caracteres.</span>
            )}
          </div>
        </div>

        <div>
          <input
            type="password"
            placeholder="Confirme a senha"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
            data-testid="accept-invite-confirm"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${touched.confirm && confirmIssue ? "border-rose-400 focus:ring-rose-500" : "border-slate-300 focus:ring-slate-900"}`}
          />
          {touched.confirm && confirmIssue && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-rose-600" data-testid="accept-invite-confirm-error">
              <span className="w-1 h-1 rounded-full bg-rose-500" />
              {confirmIssue}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="accept-invite-submit"
          className="w-full py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-2 mt-2"
        >
          {submitting ? (<><Sparkle size={14} weight="fill" className="animate-pulse" /> Entrando…</>) : "Criar senha e entrar"}
        </button>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-8">
        {children}
      </div>
    </div>
  );
}
